import sample from "lodash/sample";
import { BranchOrientation } from "../enum/BranchOrientation";
import { NodeType } from "../enum/NodeType";
import type { ContentNode } from "../interfaces/ContentNode";
import type { Root } from "../interfaces/Root";
import type { SubBranch } from "../interfaces/SubBranch";
import type { Nodes } from "../structure/Compartment";
import { randomInt } from "./ContentTree";

export interface Mutation {
  weight: number;
  mutation: (node: Root) => unknown;
}

export function popRandom<type = unknown>(array: type[]) {
  return array.splice(randomInt(array.length), 1)[0];
}
export function pushRandom<type = unknown>(array: type[], element: type) {
  return array.splice(randomInt(array.length), 0, element);
}

/**
 * recurse over all subnodes and call optional function
 * @param node
 * @param fn
 */
export function recurse<ReturnType = Nodes>(
  node: Nodes,
  fn: (n: Nodes) => ReturnType = (n) => n as unknown as ReturnType,
): ReturnType[] {
  const result: ReturnType[] = [];
  const r = (n: Nodes) => {
    if (n.type === NodeType.BRANCH) {
      for (const child of n.children) {
        r(child);
      }
    }
    result.push(fn(n));
  };
  r(node);
  return result;
}

export function getAllSubnodes(node: Nodes, type?: NodeType): Nodes[] {
  if (type !== undefined) {
    return recurse(node, (n) => n).filter((n) => n.type === type);
  } else {
    return recurse(node, (n) => n);
  }
}
export function getRandomNode(nodes: Nodes, type: NodeType.ITEM): ContentNode;
export function getRandomNode(nodes: Nodes, type: NodeType.BRANCH): SubBranch;
export function getRandomNode(nodes: Nodes, type?: NodeType): Nodes {
  const candidates = getAllSubnodes(nodes, type);
  if (candidates.length === 0) {
    // Wenn keine Kandidaten gefunden werden, gib den Root-Knoten zurück
    // aber nur wenn der Typ passt oder kein Typ spezifiziert wurde
    if (type === undefined || nodes.type === type) {
      return nodes;
    }
    // Wenn der Typ nicht passt, versuche den ersten passenden Kind-Knoten zu finden
    if (nodes.type === NodeType.BRANCH && nodes.children.length > 0) {
      const firstChild = nodes.children[0];
      if (firstChild && (type === undefined || firstChild.type === type)) {
        return firstChild;
      }
    }
    // Fallback: gib den Root zurück
    return nodes;
  }
  const picked = sample(candidates);
  if (!picked) {
    // Fallback falls sample undefined zurückgibt
    return candidates[0] ?? nodes;
  }
  return picked;
}

export const mutations: { [id: string]: Mutation } = {
  // nodeToRandom: {
  //     weight: 4,
  //     mutation(node: Root) {
  //         const sourceNode = getRandomNode(node, NodeType.BRANCH);
  //         const targetNode = getRandomNode(node, NodeType.BRANCH);
  //         pushRandom(targetNode.children, popRandom(sourceNode.children));
  //     },
  // },
  reorder: {
    weight: 1,
    mutation(node: Root) {
      if (!node.children.length) {
        return;
      }
      return pushRandom(node.children, popRandom(node.children));
    },
  },
  rotateAnItem: {
    weight: 1,
    mutation(node: Root) {
      const randomNode = getRandomNode(node, NodeType.ITEM);
      if (randomNode && randomNode.type === NodeType.ITEM) {
        randomNode.orientation = Math.floor(Math.random() * 6);
      }
    },
  },
  rotateMultipleItemsOfABranch: {
    weight: 1,
    mutation(node: Root) {
      const randomNode = getRandomNode(node, NodeType.BRANCH);
      if (
        randomNode &&
        randomNode.type === NodeType.BRANCH &&
        randomNode.children
      ) {
        for (const child of randomNode.children) {
          if (child.type === NodeType.ITEM) {
            child.orientation = Math.floor(Math.random() * 6);
          }
        }
      }
    },
  },
  splitBranch: {
    weight: 1,
    mutation(node: Root) {
      const randomNode = getRandomNode(node, NodeType.BRANCH);
      if (
        !randomNode ||
        randomNode.type !== NodeType.BRANCH ||
        !randomNode.children ||
        randomNode.children.length < 2
      ) {
        return;
      }
      randomNode.children = [
        {
          children: randomNode.children.splice(
            0,
            Math.floor(Math.random() * randomNode.children.length - 1),
          ),
          orientation:
            Math.random() < 0.5
              ? BranchOrientation.Horizontal
              : BranchOrientation.Vertical,
          type: NodeType.BRANCH,
        },
        {
          children: randomNode.children.splice(0),
          orientation:
            Math.random() < 0.5
              ? BranchOrientation.Horizontal
              : BranchOrientation.Vertical,
          type: NodeType.BRANCH,
        },
      ];
    },
  },
  swapBranchOrientation: {
    weight: 1,
    mutation(node: Root) {
      const randomNode = getRandomNode(node, NodeType.BRANCH);
      if (
        randomNode &&
        randomNode.type === NodeType.BRANCH &&
        typeof randomNode.orientation !== "undefined"
      ) {
        randomNode.orientation =
          randomNode.orientation === BranchOrientation.Horizontal
            ? BranchOrientation.Vertical
            : BranchOrientation.Horizontal;
      }
    },
  },
};
